import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="keyboard-shortcuts"
export default class extends Controller {
  static values = {
    enabled: { type: Boolean, default: true }
  }

  connect() {
    if (this.enabledValue) {
      document.addEventListener('keydown', this.handleKeydown)
      this.showShortcutsHint()
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.hideShortcutsHint()
  }

  handleKeydown = (event) => {
    // Don't trigger shortcuts when user is typing in input fields
    if (this.isTyping(event.target)) {
      return
    }

    // Handle different key combinations
    switch(event.key.toLowerCase()) {
      case '/':
        event.preventDefault()
        this.focusSearch()
        break
      case 'n':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigateToNewPost()
        }
        break
      case 'h':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigateHome()
        }
        break
      case 'c':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigateToCoaches()
        }
        break
      case 'o':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigateToOrganizations()
        }
        break
      case '?':
        event.preventDefault()
        this.showShortcutsModal()
        break
      case 'escape':
        this.closeModals()
        break
      case 'j':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigateNext()
        }
        break
      case 'k':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.navigatePrevious()
        }
        break
      case 'r':
        if (!event.metaKey && !event.ctrlKey) {
          event.preventDefault()
          this.replyToCurrentPost()
        }
        break
    }
  }

  isTyping(element) {
    const typingElements = ['INPUT', 'TEXTAREA', 'SELECT']
    return typingElements.includes(element.tagName) || 
           element.contentEditable === 'true' ||
           element.closest('[contenteditable="true"]')
  }

  focusSearch() {
    const searchInput = document.querySelector('#coach_search, #organization_search, input[type="search"], input[placeholder*="Search"]')
    if (searchInput) {
      searchInput.focus()
      searchInput.select()
    }
  }

  navigateToNewPost() {
    const newPostLink = document.querySelector('a[href*="/posts/new"], a[href*="new_post"]')
    if (newPostLink) {
      newPostLink.click()
    }
  }

  navigateHome() {
    window.location.href = '/'
  }

  navigateToCoaches() {
    window.location.href = '/coaches'
  }

  navigateToOrganizations() {
    window.location.href = '/organizations'
  }

  navigateNext() {
    // Navigate to next post or comment
    const currentPost = document.querySelector('.post-item.current, [data-post-item].current')
    if (currentPost) {
      const nextPost = currentPost.nextElementSibling
      if (nextPost) {
        this.highlightPost(nextPost)
        nextPost.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    } else {
      // Highlight first post
      const firstPost = document.querySelector('[data-post-item]')
      if (firstPost) {
        this.highlightPost(firstPost)
      }
    }
  }

  navigatePrevious() {
    // Navigate to previous post or comment
    const currentPost = document.querySelector('.post-item.current, [data-post-item].current')
    if (currentPost) {
      const prevPost = currentPost.previousElementSibling
      if (prevPost && prevPost.matches('[data-post-item]')) {
        this.highlightPost(prevPost)
        prevPost.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    } else {
      // Highlight last post
      const posts = document.querySelectorAll('[data-post-item]')
      const lastPost = posts[posts.length - 1]
      if (lastPost) {
        this.highlightPost(lastPost)
      }
    }
  }

  highlightPost(post) {
    // Remove current highlight
    document.querySelectorAll('.current').forEach(el => {
      el.classList.remove('current', 'ring-2', 'ring-blue-500')
    })
    
    // Add highlight to new post
    post.classList.add('current', 'ring-2', 'ring-blue-500')
  }

  replyToCurrentPost() {
    const currentPost = document.querySelector('.post-item.current, [data-post-item].current')
    if (currentPost) {
      const replyForm = currentPost.querySelector('textarea[placeholder*="thoughts"], textarea[data-mention-target="input"]')
      if (replyForm) {
        replyForm.focus()
        replyForm.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  showShortcutsModal() {
    // Create and show shortcuts modal
    const modal = this.createShortcutsModal()
    document.body.appendChild(modal)
    
    // Focus the modal for keyboard navigation
    modal.querySelector('.modal-content').focus()
  }

  closeModals() {
    // Close any open modals
    const modals = document.querySelectorAll('.shortcuts-modal, [data-modal], .modal')
    modals.forEach(modal => {
      modal.remove()
    })
    
    // Remove any dropdowns
    const dropdowns = document.querySelectorAll('[data-dropdown-target="menu"]:not(.hidden)')
    dropdowns.forEach(dropdown => {
      dropdown.classList.add('hidden')
    })
  }

  createShortcutsModal() {
    const modal = document.createElement('div')
    modal.className = 'shortcuts-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
    modal.innerHTML = `
      <div class="modal-content bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto" tabindex="-1">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h2>
          <button onclick="this.closest('.shortcuts-modal').remove()" class="text-gray-400 hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="space-y-3 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Search</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">/</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">New Post</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">n</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Home</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">h</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Coaches</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">c</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Organizations</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">o</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Next Post</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">j</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Previous Post</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">k</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Reply</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">r</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Close Modal</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Show Shortcuts</span>
            <kbd class="px-2 py-1 bg-gray-100 rounded text-xs">?</kbd>
          </div>
        </div>
      </div>
    `
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove()
      }
    })
    
    return modal
  }

  showShortcutsHint() {
    // Removed — was showing a blue popup below the dock on mobile
  }

  hideShortcutsHint() {
  }
}